'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { Bell, Sparkles, ChevronDown, User, Settings, CreditCard, Coins, LogOut, X, CheckCircle, Save, AlertCircle, Menu, ChevronRight, Home, Dna, FileCode, Image, Video, Mic, Share2, Wrench } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';
import { useLanguage } from '@/context/LanguageContext';
import GlobalSearch from '@/components/GlobalSearch';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { toast } from 'sonner';

import { resolveUserDisplayIdentity } from '@/lib/userDisplayIdentity';

interface AppTopbarProps {
  sidebarCollapsed: boolean;
  title?: string;
  onMobileMenuToggle?: () => void;
}

interface Notification {
  id: string;
  type: 'success' | 'credit' | 'save' | 'billing' | 'info';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

const NOTIFICATION_ICONS: Record<string, React.ReactNode> = {
  success: <CheckCircle size={14} className="text-green-400" />,
  credit: <CreditCard size={14} className="text-blue-400" />,
  save: <Save size={14} className="text-purple-400" />,
  billing: <AlertCircle size={14} className="text-yellow-400" />,
  info: <Bell size={14} className="text-muted-foreground" />,
};

// ─── Route → breadcrumb label map ─────────────────────────────────────────────

const ROUTE_LABELS: Record<Language, Record<string, string>> = {
  en: {
    '/welcome': 'Welcome Hub',
    '/dream-dashboard': 'Dream Dashboard™',
    '/': 'Dream Builder™',
    '/my-projects': 'My Projects',
    '/business-knowledge-vault': 'Knowledge Vault™',
    '/business-builder': 'Business Builder',
    '/image-studio': 'Image Studio',
    '/ai-tools-page': 'AI Tools',
    '/my-library': 'My Library',
    '/resources': 'Resources',
    '/pricing': 'Pricing',
    '/account': 'Account Center',
    '/profile': 'Profile',
    '/credits': 'Credits',
    '/user-dashboard': 'Dashboard',
    '/dream-workspace': 'Dream Workspace',
    '/svg-studio': 'SVG Studio',
    '/social-media-studio': 'Social Media Studio',
    '/video-studio': 'Video Studio',
    '/voice-studio': 'Voice Studio',
    '/avatar-studio': 'Avatar Studio',
    '/3d-studio': '3D Studio',
    '/brand-assets': 'Brand Assets',
    '/brand-refresh': 'Brand Refresh',
    '/templates': 'Templates',
    '/blog': 'Blog',
    '/about': 'About',
    '/contact': 'Contact',
    '/help-center': 'Help Center',
    '/learn-center': 'Learn Center',
    '/security': 'Security',
    '/status': 'Status',
    '/careers': 'Careers',
    '/projects-page': 'Projects',
    '/create-page': 'Create',
    '/account-settings': 'Account Settings',
    '/onboarding': 'Onboarding',
    '/new-dream': 'New DreamDNA™',
  },
  pt: {
    '/welcome': 'Hub de Boas-Vindas',
    '/dream-dashboard': 'Dream Dashboard™',
    '/': 'Dream Builder™',
    '/my-projects': 'Meus Projetos',
    '/business-knowledge-vault': 'Cofre de Conhecimento™',
    '/business-builder': 'Construtor de Negócios',
    '/image-studio': 'Estúdio de Imagens',
    '/ai-tools-page': 'Ferramentas IA',
    '/my-library': 'Minha Biblioteca',
    '/resources': 'Recursos',
    '/pricing': 'Preços',
    '/account': 'Central da Conta',
    '/profile': 'Perfil',
    '/credits': 'Créditos',
    '/user-dashboard': 'Painel',
    '/dream-workspace': 'Espaço de Trabalho',
    '/svg-studio': 'Estúdio SVG',
    '/social-media-studio': 'Estúdio de Redes Sociais',
    '/video-studio': 'Estúdio de Vídeo',
    '/voice-studio': 'Estúdio de Voz',
    '/avatar-studio': 'Estúdio de Avatar',
    '/3d-studio': 'Estúdio 3D',
    '/brand-assets': 'Ativos de Marca',
    '/brand-refresh': 'Renovação de Marca',
    '/templates': 'Modelos',
    '/blog': 'Blog',
    '/about': 'Sobre',
    '/contact': 'Contato',
    '/help-center': 'Central de Ajuda',
    '/learn-center': 'Central de Aprendizado',
    '/security': 'Segurança',
    '/status': 'Status',
    '/careers': 'Carreiras',
    '/projects-page': 'Projetos',
    '/create-page': 'Criar',
    '/account-settings': 'Configurações da Conta',
    '/onboarding': 'Introdução',
    '/new-dream': 'Novo DreamDNA™',
  },
  es: {
    '/welcome': 'Hub de Bienvenida',
    '/dream-dashboard': 'Dream Dashboard™',
    '/': 'Dream Builder™',
    '/my-projects': 'Mis Proyectos',
    '/business-knowledge-vault': 'Bóveda de Conocimiento™',
    '/business-builder': 'Constructor de Negocios',
    '/image-studio': 'Estudio de Imágenes',
    '/ai-tools-page': 'Herramientas IA',
    '/my-library': 'Mi Biblioteca',
    '/resources': 'Recursos',
    '/pricing': 'Precios',
    '/account': 'Centro de Cuenta',
    '/profile': 'Perfil',
    '/credits': 'Créditos',
    '/user-dashboard': 'Panel',
    '/dream-workspace': 'Espacio de Trabajo',
    '/svg-studio': 'Estudio SVG',
    '/social-media-studio': 'Estudio de Redes Sociales',
    '/video-studio': 'Estudio de Video',
    '/voice-studio': 'Estudio de Voz',
    '/avatar-studio': 'Estudio de Avatar',
    '/3d-studio': 'Estudio 3D',
    '/brand-assets': 'Activos de Marca',
    '/brand-refresh': 'Renovación de Marca',
    '/templates': 'Plantillas',
    '/blog': 'Blog',
    '/about': 'Acerca de',
    '/contact': 'Contacto',
    '/help-center': 'Centro de Ayuda',
    '/learn-center': 'Centro de Aprendizaje',
    '/security': 'Seguridad',
    '/status': 'Estado',
    '/careers': 'Carreras',
    '/projects-page': 'Proyectos',
    '/create-page': 'Crear',
    '/account-settings': 'Configuración de Cuenta',
    '/onboarding': 'Introducción',
    '/new-dream': 'Nuevo DreamDNA™',
  },
};

// Pages that are "top-level" — no back button needed
const TOP_LEVEL_ROUTES = new Set(['/welcome', '/dream-dashboard', '/', '/my-projects', '/user-dashboard', '/pricing', '/account', '/dream-workspace']);

type Language = 'en' | 'pt' | 'es';

export default function AppTopbar({ sidebarCollapsed, title, onMobileMenuToggle }: AppTopbarProps) {
  const { t, language } = useLanguage();
  const { user, signOut, loading: authLoading } = useAuth();
  const { credits } = useSubscription();
  const router = useRouter();
  const pathname = usePathname();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [notifications] = useState<Notification[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const createRef = useRef<HTMLDivElement>(null);

  // Never show demo/mock names (e.g. "Jordan M." / "JM") when session is clearing or absent.
  const identity =
    signingOut || authLoading || !user ? null : resolveUserDisplayIdentity(user);
  const unreadCount = notifications.filter(n => !n.read).length;
  const creditBalance = credits?.balance;

  // Breadcrumb logic
  const lang = (language as Language) in ROUTE_LABELS ? (language as Language) : 'en';
  const routeLabels = ROUTE_LABELS[lang];
  const fallbackTitle =
    title === 'MyProjects' || title === 'Myprojects'
      ? routeLabels['/my-projects']
      : title;
  const currentLabel = routeLabels[pathname] || fallbackTitle || '';
  const isTopLevel = TOP_LEVEL_ROUTES.has(pathname);
  const showBackButton = !isTopLevel && pathname !== '/';
  const homeLabel = lang === 'pt' ? 'Início' : lang === 'es' ? 'Inicio' : 'Home';
  const backLabel = lang === 'pt' ? 'Voltar' : lang === 'es' ? 'Volver' : 'Back';

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setDropdownOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (createRef.current && !createRef.current.contains(e.target as Node)) setCreateOpen(false);
    }
    if (dropdownOpen || notifOpen || createOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen, notifOpen, createOpen]);

  const handleSignOut = async () => {
    setDropdownOpen(false);
    setSigningOut(true);
    try {
      await signOut();
      toast.success(t?.nav?.signOut || 'Signed out successfully');
      router.push('/sign-up-login-screen');
    } catch {
      setSigningOut(false);
      toast.error(t?.errors?.generic || 'Failed to sign out');
    }
  };

  const markAllRead = () => {};

  const menuItems = [
    { label: t?.nav?.profile || 'Profile', href: '/account', icon: User },
    { label: t?.nav?.accountSettings || 'Account Center', href: '/account', icon: Settings },
    { label: t?.nav?.billing || 'Billing', href: '/pricing', icon: CreditCard },
    { label: t?.nav?.credits || 'Credits', href: '/credits', icon: Coins },
  ];

  return (
    <header
      className={`fixed top-0 right-0 z-30 h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8 bg-background/90 backdrop-blur-xl border-b border-border transition-all duration-300
        left-0
        ${sidebarCollapsed ? 'lg:left-16' : 'lg:left-64'}
      `}
    >
      <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
        {/* Mobile hamburger */}
        <button
          onClick={onMobileMenuToggle}
          className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors shrink-0"
          aria-label="Toggle menu"
        >
          <Menu size={20} />
        </button>

        {/* Breadcrumb / Back navigation */}
        <div className="flex items-center gap-2 min-w-0">
          {/* Home shortcut — always visible on non-home pages */}
          {pathname !== '/welcome' && pathname !== '/' && (
            <Link
              href="/welcome"
              className="hidden sm:flex items-center gap-1 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors shrink-0"
              title={homeLabel}
              aria-label={homeLabel}
            >
              <Home size={15} />
            </Link>
          )}

          {/* Breadcrumb separator */}
          {pathname !== '/welcome' && pathname !== '/' && currentLabel && (
            <ChevronRight size={13} className="text-muted-foreground/40 shrink-0 hidden sm:block" />
          )}

          {/* Back button for deep pages */}
          {showBackButton && (
            <button
              onClick={() => router.back()}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-500 text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors shrink-0 sm:hidden"
              aria-label={backLabel}
            >
              ← {backLabel}
            </button>
          )}

          {/* Current page label */}
          {currentLabel && (
            <span className="text-[13px] font-medium text-foreground truncate max-w-[120px] sm:max-w-[180px] md:max-w-[240px] hidden sm:block tracking-tight">
              {currentLabel}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
        {user && typeof creditBalance === 'number' && (
          <Link
            href="/credits"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-card/60 text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-primary/5 transition-colors"
            title={t?.nav?.credits || 'Credits'}
            aria-label={`${t?.nav?.credits || 'Credits'}: ${creditBalance}`}
          >
            <Coins size={14} className="text-primary shrink-0" />
            <span className="text-xs font-700 text-foreground tabular-nums">{creditBalance}</span>
            <span className="text-[10px] text-muted-foreground hidden sm:inline">
              {t?.nav?.credits || 'Credits'}
            </span>
          </Link>
        )}

        <GlobalSearch />

        {/* Notification Bell */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setNotifOpen(prev => !prev)}
            className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            title={t?.nav?.notifications || 'Notifications'}
          >
            <Bell size={18} />
            {unreadCount > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent" />}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 rounded-xl bg-card border border-border shadow-2xl overflow-hidden dropdown-enter" style={{ zIndex: 9999 }}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div>
                  <p className="text-sm font-600 text-foreground">{t?.notifications?.title || 'Notifications'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && <button onClick={markAllRead} className="text-[11px] text-primary hover:text-primary/80 transition-colors">{t?.notifications?.markAllRead || 'Mark all read'}</button>}
                  <button onClick={() => setNotifOpen(false)} className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"><X size={14} /></button>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                    <Bell size={28} className="text-muted-foreground/30 mb-3" />
                    <p className="text-sm font-500 text-foreground mb-1">{t?.notifications?.empty || 'No notifications yet'}</p>
                    <p className="text-xs text-muted-foreground">{t?.notifications?.emptyDesc || "We'll notify you when something happens."}</p>
                  </div>
                ) : (
                  <div className="py-1">
                    {notifications.map(notif => (
                      <div key={notif.id} className={`px-4 py-3 border-b border-border/50 last:border-0 transition-colors ${!notif.read ? 'bg-primary/3' : ''}`}>
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 shrink-0">{NOTIFICATION_ICONS[notif.type]}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <p className={`text-xs font-600 truncate ${!notif.read ? 'text-foreground' : 'text-muted-foreground'}`}>{notif.title}</p>
                              {!notif.read && <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />}
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">{notif.message}</p>
                            <p className="text-[10px] text-muted-foreground/60 mt-1">{notif.time}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-4 py-2.5 border-t border-border bg-muted/10">
                <p className="text-[10px] text-muted-foreground text-center">{t?.notifications?.preferences || 'Notification preferences available in Account Settings'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Language Selector */}
        <LanguageSelector />

        {user && (
          <div className="relative hidden sm:block" ref={createRef}>
            <button
              onClick={() => setCreateOpen(prev => !prev)}
              className="btn-primary px-3 sm:px-4 py-2 rounded-lg text-sm flex items-center gap-2"
              aria-haspopup="true"
              aria-expanded={createOpen}
              aria-label={t?.nav?.create || 'Create'}
            >
              <Sparkles size={14} />
              <span className="hidden md:inline">{t?.nav?.create || 'Create'}</span>
              <ChevronDown size={13} className={`transition-transform duration-200 ${createOpen ? 'rotate-180' : ''}`} />
            </button>

            {createOpen && (
              <div
                className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-card border border-border shadow-2xl overflow-hidden dropdown-enter"
                style={{ zIndex: 9999, maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}
                role="menu"
              >
                <div className="py-1">
                  {/* 1. New DreamDNA™ */}
                  <Link
                    href="/new-dream"
                    onClick={() => setCreateOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-foreground hover:bg-primary/8 hover:text-primary transition-colors focus:outline-none focus:bg-primary/8"
                    role="menuitem"
                  >
                    <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                      <Dna size={14} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-600 text-xs leading-tight">
                        {lang === 'pt' ? 'Novo DreamDNA™' : lang === 'es' ? 'Nuevo DreamDNA™' : 'New DreamDNA™'}
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                        {lang === 'pt' ? 'Analisar uma nova ideia' : lang === 'es' ? 'Analizar una nueva idea' : 'Analyze a new dream idea'}
                      </p>
                    </div>
                  </Link>

                  {/* 2. Image Studio */}
                  <Link
                    href="/image-studio"
                    onClick={() => setCreateOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors focus:outline-none focus:bg-white/5"
                    role="menuitem"
                  >
                    <div className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center shrink-0">
                      <Image size={14} className="text-blue-400" />
                    </div>
                    <div>
                      <p className="font-600 text-xs leading-tight">
                        {t?.nav?.imageStudio || 'Image Studio'}
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                        {lang === 'pt' ? 'Gerar imagens com IA' : lang === 'es' ? 'Generar imágenes con IA' : 'AI image generation'}
                      </p>
                    </div>
                  </Link>

                  {/* 3. Video Studio */}
                  <Link
                    href="/video-studio"
                    onClick={() => setCreateOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors focus:outline-none focus:bg-white/5"
                    role="menuitem"
                  >
                    <div className="w-7 h-7 rounded-lg bg-purple-500/15 flex items-center justify-center shrink-0">
                      <Video size={14} className="text-purple-400" />
                    </div>
                    <div>
                      <p className="font-600 text-xs leading-tight">
                        {t?.nav?.videoStudio || 'Video Studio'}
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                        {lang === 'pt' ? 'Gerar vídeos com IA' : lang === 'es' ? 'Generar videos con IA' : 'AI video generation'}
                      </p>
                    </div>
                  </Link>

                  {/* 4. SVG Studio */}
                  <Link
                    href="/svg-studio"
                    onClick={() => setCreateOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors focus:outline-none focus:bg-white/5"
                    role="menuitem"
                  >
                    <div className="w-7 h-7 rounded-lg bg-yellow-500/15 flex items-center justify-center shrink-0">
                      <FileCode size={14} className="text-yellow-400" />
                    </div>
                    <div>
                      <p className="font-600 text-xs leading-tight">
                        {t?.nav?.svgStudio || 'SVG Studio'}
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                        {lang === 'pt' ? 'Vetores, adesivos e logos' : lang === 'es' ? 'Vectores, stickers y logos' : 'Vectors, stickers & logos'}
                      </p>
                    </div>
                  </Link>

                  {/* 5. Voice/Podcast Studio */}
                  <Link
                    href="/voice-studio"
                    onClick={() => setCreateOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors focus:outline-none focus:bg-white/5"
                    role="menuitem"
                  >
                    <div className="w-7 h-7 rounded-lg bg-green-500/15 flex items-center justify-center shrink-0">
                      <Mic size={14} className="text-green-400" />
                    </div>
                    <div>
                      <p className="font-600 text-xs leading-tight">
                        {t?.nav?.voiceStudio || 'Voice Studio'}
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                        {lang === 'pt' ? 'Voz e podcast com IA' : lang === 'es' ? 'Voz y podcast con IA' : 'AI voice & podcast'}
                      </p>
                    </div>
                  </Link>

                  {/* 6. Social Post Studio */}
                  <Link
                    href="/social-media-studio"
                    onClick={() => setCreateOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors focus:outline-none focus:bg-white/5"
                    role="menuitem"
                  >
                    <div className="w-7 h-7 rounded-lg bg-pink-500/15 flex items-center justify-center shrink-0">
                      <Share2 size={14} className="text-pink-400" />
                    </div>
                    <div>
                      <p className="font-600 text-xs leading-tight">
                        {t?.nav?.socialMediaStudio || 'Social Post Studio'}
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                        {lang === 'pt' ? 'Posts para redes sociais' : lang === 'es' ? 'Posts para redes sociales' : 'Social media content'}
                      </p>
                    </div>
                  </Link>

                  {/* Divider */}
                  <div className="border-t border-border/50 my-1" />

                  {/* 7. View All AI Tools */}
                  <Link
                    href="/ai-tools-page"
                    onClick={() => setCreateOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors focus:outline-none focus:bg-white/5"
                    role="menuitem"
                  >
                    <div className="w-7 h-7 rounded-lg bg-accent/15 flex items-center justify-center shrink-0">
                      <Wrench size={14} className="text-accent" />
                    </div>
                    <div>
                      <p className="font-600 text-xs leading-tight">
                        {t?.nav?.viewAllAiTools || 'View All AI Tools'}
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                        {lang === 'pt' ? 'Ver todas as ferramentas' : lang === 'es' ? 'Ver todas las herramientas' : 'Browse the full toolkit'}
                      </p>
                    </div>
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Profile Dropdown — neutral placeholder while loading / signing out / signed out */}
        <div className="relative" ref={dropdownRef}>
          {identity ? (
            <>
              <button
                type="button"
                onClick={() => setDropdownOpen(prev => !prev)}
                className="flex items-center gap-1.5 sm:gap-2 pl-2 pr-2.5 sm:pr-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors cursor-pointer select-none"
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
                aria-label={identity.fullName}
              >
                <div className="w-7 h-7 rounded-full gradient-primary flex items-center justify-center text-xs font-700 text-white shrink-0">
                  {identity.initials}
                </div>
                <span className="text-sm font-medium text-foreground hidden md:block">{identity.fullName}</span>
                <ChevronDown size={14} className={`text-muted-foreground transition-transform duration-200 hidden sm:block ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 sm:w-56 rounded-xl bg-card border border-border shadow-2xl overflow-hidden dropdown-enter" style={{ zIndex: 9999 }}>
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-sm font-600 text-foreground truncate">{identity.fullName}</p>
                    {identity.email && (
                      <p className="text-xs text-muted-foreground truncate">{identity.email}</p>
                    )}
                  </div>
                  <div className="py-1">
                    {menuItems.map(item => {
                      const ItemIcon = item.icon;
                      return (
                        <Link key={item.href + item.label} href={item.href} onClick={() => setDropdownOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
                          <ItemIcon size={15} />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                  <div className="border-t border-border py-1">
                    <button type="button" onClick={handleSignOut} className="flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:text-red-400 hover:bg-red-400/5 transition-colors w-full text-left">
                      <LogOut size={15} />
                      {t?.nav?.signOut || 'Sign Out'}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div
              className="flex items-center gap-1.5 sm:gap-2 pl-2 pr-2.5 sm:pr-3 py-1.5"
              aria-hidden="true"
              data-testid="auth-identity-placeholder"
            >
              <div className="w-7 h-7 rounded-full bg-muted/40 animate-pulse shrink-0" />
              <span className="hidden md:block h-3.5 w-20 rounded bg-muted/40 animate-pulse" />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}